import { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import { isAuthenticated, getAuthToken, getUserProfile, logout as logoutUser, getToken, exchangeMagicToken, getMyMemberProfile, logger } from '@kuow/core';
import type { User, AuthToken, MemberProfile } from '@kuow/core';

// Auth state interface
interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  member: MemberProfile | null;
  token: AuthToken | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

// Auth actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; member: MemberProfile | null; token: AuthToken } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_MEMBER'; payload: MemberProfile | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_INITIALIZED' };

// Auth context interface
interface AuthContextValue extends AuthState {
  login: (username?: string, password?: string, remember?: boolean) => Promise<boolean>;
  loginWithMagicToken: (magicToken: string, remember?: boolean) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  refreshMember: () => Promise<void>;
}

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  member: null,
  token: null,
  loading: true,
  error: null,
  initialized: false,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        member: action.payload.member,
        token: action.payload.token,
        loading: false,
        error: null,
        initialized: true,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        member: null,
        token: null,
        loading: false,
        error: action.payload,
        initialized: true,
      };
    case 'AUTH_LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        member: null,
        token: null,
        loading: false,
        error: null,
        initialized: true,
      };
    case 'SET_USER':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload,
        loading: false,
        error: null,
        initialized: true,
      };
    case 'SET_MEMBER':
      return {
        ...state,
        member: action.payload,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_INITIALIZED':
      return {
        ...state,
        initialized: true,
        loading: false,
      };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const fetchMemberSafe = async (): Promise<MemberProfile | null> => {
  try {
    return await getMyMemberProfile();
  } catch (error) {
    logger.error('Failed to load member profile', { error });
    return null;
  }
};

// Auth provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const initializingRef = useRef(false);

  // Initialize auth state on mount
  useEffect(() => {
    // Prevent multiple simultaneous initialization calls
    if (initializingRef.current || state.initialized) {
      return;
    }

    const initializeAuth = async () => {
      initializingRef.current = true;
      
      try {
        const currentlyAuthenticated = isAuthenticated();
        const currentToken = getAuthToken();
        
        if (!currentlyAuthenticated || !currentToken) {
          dispatch({ type: 'SET_INITIALIZED' });
          return;
        }

        dispatch({ type: 'AUTH_START' });
        const [userData, memberData] = await Promise.all([
          getUserProfile(),
          fetchMemberSafe(),
        ]);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: userData, member: memberData, token: currentToken },
        });
      } catch (error) {
        logger.error('Failed to initialize auth', { error });
        dispatch({ 
          type: 'AUTH_FAILURE', 
          payload: error instanceof Error ? error.message : 'Authentication failed' 
        });
      } finally {
        initializingRef.current = false;
      }
    };

    void initializeAuth();
  }, [state.initialized]); // Only run when not initialized

  // Login function
  const login = async (username?: string, password?: string, remember = true): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const result = await getToken(username, password, remember);
      if (result?.token) {
        const [userData, memberData] = await Promise.all([
          getUserProfile(),
          fetchMemberSafe(),
        ]);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: userData, member: memberData, token: result },
        });
        return true;
      }

      dispatch({ type: 'AUTH_FAILURE', payload: 'Login failed' });
      return false;
    } catch (error) {
      logger.error('Login failed', { error });
      dispatch({ 
        type: 'AUTH_FAILURE', 
        payload: error instanceof Error ? error.message : 'Login failed' 
      });
      return false;
    }
  };

  const loginWithMagicToken = async (magicToken: string, remember = true): Promise<boolean> => {
    dispatch({ type: 'AUTH_START' });

    try {
      const result = await exchangeMagicToken(magicToken, remember);
      if (result?.token) {
        const [userData, memberData] = await Promise.all([
          getUserProfile(),
          fetchMemberSafe(),
        ]);
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: userData, member: memberData, token: result },
        });
        return true;
      }

      dispatch({ type: 'AUTH_FAILURE', payload: 'Magic link sign-in failed' });
      return false;
    } catch (error) {
      logger.error('Magic link sign-in failed', { error });
      dispatch({
        type: 'AUTH_FAILURE',
        payload: error instanceof Error ? error.message : 'Magic link sign-in failed',
      });
      return false;
    }
  };

  // Logout function
  const logout = () => {
    logoutUser();
    dispatch({ type: 'AUTH_LOGOUT' });
  };

  const refreshUser = async (): Promise<void> => {
    const currentToken = getAuthToken();
    const currentlyAuthenticated = isAuthenticated();
    if (!currentlyAuthenticated || !currentToken) return;

    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      const userData = await getUserProfile();
      dispatch({ type: 'SET_USER', payload: userData });
    } catch (error) {
      logger.error('Failed to refresh user profile', { error });
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const refreshMember = async (): Promise<void> => {
    const currentToken = getAuthToken();
    const currentlyAuthenticated = isAuthenticated();
    if (!currentlyAuthenticated || !currentToken) return;

    try {
      const memberData = await getMyMemberProfile();
      dispatch({ type: 'SET_MEMBER', payload: memberData });
    } catch (error) {
      logger.error('Failed to refresh member profile', { error });
    }
  };

  // Clear error function
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextValue = {
    ...state,
    login,
    loginWithMagicToken,
    logout,
    clearError,
    refreshUser,
    refreshMember,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 